'use client';

import { useState, useEffect, useCallback } from 'react';
import type { Feedback, FeedbackStatus, FeedbackSeverity } from '@/lib/types/feedback';
import { CATEGORY_LABELS, SEVERITY_LABELS, STATUS_LABELS } from '@/lib/types/feedback';

const CEO_PIN = '5050';

const SEVERITY_COLORS: Record<FeedbackSeverity, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200',
};

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  triaged: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  wont_fix: 'bg-gray-100 text-gray-800',
};

function timeAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function FeedbackTriagePage() {
  const [pin, setPin] = useState('');
  const [authed, setAuthed] = useState(false);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [adminNotes, setAdminNotes] = useState('');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSeverity, setFilterSeverity] = useState<string>('');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterSeverity) params.set('severity', filterSeverity);

      const res = await fetch(`/api/feedback?${params}`, {
        headers: { 'x-ceo-pin': CEO_PIN },
      });
      const json = await res.json();
      setFeedback(json.data || []);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  useEffect(() => {
    if (authed) fetchFeedback();
  }, [authed, fetchFeedback]);

  const updateFeedback = async (id: string, updates: { status?: FeedbackStatus; severity?: FeedbackSeverity; admin_notes?: string }) => {
    try {
      const res = await fetch(`/api/feedback/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-ceo-pin': CEO_PIN,
        },
        body: JSON.stringify(updates),
      });

      if (res.ok) {
        fetchFeedback();
        if (selected?.id === id) {
          const json = await res.json();
          setSelected(json.data);
        }
      }
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-xl p-6 w-full max-w-xs">
          <h1 className="text-white text-lg font-bold mb-4 text-center">🔐 Feedback Triage</h1>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pin === CEO_PIN) setAuthed(true);
            }}
            placeholder="Enter PIN"
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 text-center text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={4}
            autoFocus
          />
          <button
            onClick={() => { if (pin === CEO_PIN) setAuthed(true); }}
            className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium transition-colors"
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  const stats = {
    total: feedback.length,
    new: feedback.filter(f => f.status === 'new').length,
    critical: feedback.filter(f => f.severity === 'critical' && f.status !== 'resolved').length,
    resolved: feedback.filter(f => f.status === 'resolved').length,
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      {/* Header */}
      <div className="border-b border-gray-800 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">📋 Feedback Triage</h1>
            <p className="text-gray-400 text-sm mt-1">
              {stats.total} total · {stats.new} new · {stats.critical} critical · {stats.resolved} resolved
            </p>
          </div>
          <div className="flex gap-2">
            <a href="/ceo" className="text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors">
              ← HQ
            </a>
            <button
              onClick={fetchFeedback}
              className="text-sm bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg transition-colors"
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Severity</option>
            <option value="critical">🔴 Critical</option>
            <option value="high">🟠 High</option>
            <option value="medium">🟡 Medium</option>
            <option value="low">🟢 Low</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-3">📭</p>
            <p>No feedback yet</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {feedback.map((item) => (
              <div
                key={item.id}
                onClick={() => { setSelected(item); setAdminNotes(item.admin_notes || ''); }}
                className={`bg-gray-900 rounded-xl p-4 border cursor-pointer transition-all hover:border-gray-600 ${
                  selected?.id === item.id ? 'border-blue-500' : 'border-gray-800'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLORS[item.severity]}`}>
                        {SEVERITY_LABELS[item.severity]}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status]}`}>
                        {STATUS_LABELS[item.status]}
                      </span>
                      <span className="text-xs text-gray-500">
                        {CATEGORY_LABELS[item.category]}
                      </span>
                    </div>
                    <h3 className="font-medium text-white truncate">{item.title}</h3>
                    <p className="text-sm text-gray-400 mt-1 line-clamp-2">{item.description}</p>
                  </div>
                  <div className="text-right text-xs text-gray-500 shrink-0">
                    <div>{timeAgo(item.created_at)}</div>
                    <div className="mt-1">{item.user_name || 'Anonymous'}</div>
                    <div className="text-gray-600">{item.user_type || '?'}</div>
                  </div>
                </div>

                {/* Quick actions when selected */}
                {selected?.id === item.id && (
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                      <p className="text-xs text-gray-500 col-span-full">Quick status:</p>
                      {(['triaged', 'in_progress', 'resolved', 'wont_fix'] as FeedbackStatus[]).map((s) => (
                        <button
                          key={s}
                          onClick={(e) => { e.stopPropagation(); updateFeedback(item.id, { status: s }); }}
                          className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${
                            item.status === s
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {STATUS_LABELS[s]}
                        </button>
                      ))}
                    </div>

                    {/* Admin notes */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Admin notes..."
                        className="flex-1 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateFeedback(item.id, { admin_notes: adminNotes });
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                      >
                        Save
                      </button>
                    </div>

                    {/* Context */}
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      {item.page_url && <div>Page: {item.page_url}</div>}
                      {item.user_email && <div>Email: {item.user_email}</div>}
                      <div>ID: {item.id}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
